import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "wouter";
import { Eye, FileText, Plus } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { DailyReportInstallationIdentity } from "@/components/reports/DailyReportInstallationIdentity";
import type { InstallationIdentityLike } from "@/lib/installationContext";
import { formatDailyReportHours } from "@shared/dailyReportHours";

interface DailyReport {
    id: number;
    installationId: number;
    userId: number;
    reportDate: Date;
    workDescription: string;
    hoursWorked: number;
    authorName?: string | null;
    signatureUrl?: string;
    signatureKey?: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export function ReportsTab({ installation }: { installation: InstallationIdentityLike }) {
    const installationId = installation.id;
    const [, setLocation] = useLocation();
    const reportsQuery = trpc.dailyReports.listByInstallation.useQuery({ installationId });

    return (
        <Card>
            <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <CardTitle>Partes Diarios</CardTitle>
                    <CardDescription>Registro de actividad diaria. Las horas indicadas en cada parte son informativas, no individuales ni de nómina.</CardDescription>
                    <DailyReportInstallationIdentity installation={installation} className="mt-2 text-xs text-muted-foreground" />
                </div>
                <Link href={`/daily-reports/new?installationId=${installationId}`}>
                    <Button size="sm" className="gap-2">
                        <Plus className="w-4 h-4" />
                        Nuevo Parte
                    </Button>
                </Link>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    {reportsQuery.isLoading ? (
                        <p className="text-muted-foreground">Cargando reportes...</p>
                    ) : reportsQuery.data?.length === 0 ? (
                        <p className="text-muted-foreground">No hay partes diarios registrados.</p>
                    ) : (
                        reportsQuery.data?.map((report: DailyReport) => (
                            <button
                                key={report.id}
                                type="button"
                                className="w-full flex flex-col gap-3 p-3 border rounded-lg bg-card hover:bg-muted/50 transition-colors text-left md:flex-row md:justify-between md:items-center"
                                onClick={() => setLocation(`/daily-reports/${report.id}?fromInstallation=${installationId}`)}
                            >
                                <div className="flex min-w-0 items-center gap-3">
                                    <FileText className="w-5 h-5 text-blue-500" />
                                    <div className="min-w-0 space-y-1">
                                        <p className="font-medium">{new Date(report.reportDate).toLocaleDateString("es-ES")}</p>
                                        <p className="text-xs text-muted-foreground">Autor del parte: {report.authorName || "Sin identificar"}</p>
                                        <p className="text-xs text-muted-foreground break-words">{report.workDescription}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                    <span title="Dato informativo; no representa horas individuales ni se usa para nómina">Horas indicadas en el parte: {formatDailyReportHours(report.hoursWorked)} h</span>
                                    {report.signatureUrl && <span className="text-green-600 font-medium">Firmado</span>}
                                    <span className="inline-flex items-center gap-1 font-medium text-foreground">
                                        <Eye className="h-4 w-4" />
                                        Ver detalle
                                    </span>
                                </div>
                            </button>
                        ))
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
